/**
 * 動的 import（`React.lazy` / `import()`）のチャンク読み込み失敗を扱うユーティリティ。
 *
 * ■ なぜ必要か
 *   ビルドごとに `assets/*.js` のファイル名（内容ハッシュ）が変わる。デプロイをまたいで
 *   開きっぱなしのタブは**古い index.html を保持したまま**なので、そこからページ遷移すると
 *   旧ハッシュのチャンクを取りに行く。S3 から消えていれば 403/404 になり、`lazy` が
 *   レンダー中に例外を投げてアプリ全体が真っ白になる。
 *
 *   S3 側でも旧アセットを一定期間残す運用にしている（`frontend/Makefile` の sync-* は
 *   assets/ を `--delete` しない）が、それを過ぎた古いタブのために、ここで検知して
 *   「新しいバージョンを取り直す」= リロードする。
 *
 * ■ ループ防止
 *   リロードしても直らない場合（例: 本当にネットワークが死んでいる）に無限リロードへ
 *   陥らないよう、直近の試行時刻を sessionStorage に記録し、クールダウン中は自動リロード
 *   しない（呼び出し側で手動リロードのボタンを出す）。
 */

/** 自動リロードの試行時刻を保持するキー（タブ単位・sessionStorage） */
const RELOAD_ATTEMPT_KEY = 'kikakugoke:chunk-reload-at';

/** この時間内に一度リロードしていたら、再度の自動リロードはしない */
const RELOAD_COOLDOWN_MS = 60_000;

/**
 * チャンク読み込み失敗のエラーメッセージ（小文字で比較）。
 * ブラウザごとに文言が違うため複数持つ。
 * - Chrome/Edge: Failed to fetch dynamically imported module
 * - Firefox:     error loading dynamically imported module
 * - Safari:      Importing a module script failed.
 */
const CHUNK_ERROR_PATTERNS = [
  'failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'importing a module script failed',
  'failed to load module script',
  'unable to preload css',
];

/** 動的 import の読み込み失敗かどうか */
export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;

  const { name, message } = error as { name?: unknown; message?: unknown };

  // webpack 系や一部ブラウザは name で判別できる
  if (name === 'ChunkLoadError') return true;

  if (typeof message !== 'string') return false;
  const normalized = message.toLowerCase();
  return CHUNK_ERROR_PATTERNS.some((pattern) => normalized.includes(pattern));
}

/** 直近にリロードを試していなければ true（= 自動リロードしてよい） */
export function shouldAutoReload(): boolean {
  try {
    const lastAttempt = Number(sessionStorage.getItem(RELOAD_ATTEMPT_KEY));
    if (!lastAttempt) return true;
    return Date.now() - lastAttempt > RELOAD_COOLDOWN_MS;
  } catch {
    // プライベートモード等で sessionStorage が使えない場合は、
    // ループを避けるため自動リロードしない
    return false;
  }
}

/** 自動リロードを試したことを記録する */
export function markAutoReloadAttempt(): void {
  try {
    sessionStorage.setItem(RELOAD_ATTEMPT_KEY, String(Date.now()));
  } catch {
    // 記録できなくても致命的ではない（この後のリロードは行う）
  }
}

/** 現在の URL を読み直す（テストから差し替えられるよう関数に切り出す） */
export function reloadNow(): void {
  window.location.reload();
}

/**
 * チャンク読み込み失敗時に、1回だけ自動リロードする。
 * @returns リロードを開始したら true、クールダウン中で何もしなかったら false
 */
export function reloadForNewVersion(): boolean {
  if (!shouldAutoReload()) return false;
  markAutoReloadAttempt();
  reloadNow();
  return true;
}
